import { EditProject, ProjectMetadataType, ProjectFormValues } from 'store/data/projects';
import { InputType, LanguageType } from 'types/index';
import { Column, Row } from './EditProjectModal.style';
import { Modal } from 'components/UI/Modal';
import { Input } from 'components/UI/Inputs/Input';
import { Spacer } from 'components/UI/Spacer';
import { RadioGroupUncontrolled } from 'components/UI/Interactables/Uncontrolled';
import { Flex } from 'components/UI/Flex';
import { Button } from 'components/UI/Interactables/Button';
import {
	useTranslation,
	useProjectById,
	usePermissions,
	useIsProjectOwner,
	useUpdateProject,
	useProjectMetadataDefinition
} from 'hooks/store';
import { useGetUserQuery } from 'features/data/useGetUserQuery';
import { useCompletedAction, useKeyPress } from 'hooks/utils';
import {
	getInitialMetadataFormValues,
	parseFormValuesToEditedProject,
	transforMetadataParametersToFormRows,
	validateProjectForm
} from 'helpers/projects/projectMetadata';
import { CreatableSelect } from 'components/UI/Interactables/CreatableSelect';
import { InfoTooltip } from 'components/UI/Interactables/InfoTooltip';
import { ProjectStatus, ProjectType } from 'types/data/projects/constants';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'hooks/ui/useReactForm';
import { InfoMessage } from 'components/UI/InfoMessage';
import { Warning } from 'components/UI/Warning';
import { useIsCodelistFeatureEnabled } from 'features/codelist/useIsCodeListEnabled';
import { useLanguage } from 'features/hooks/useLanguage';
import { CodelistRadioGroup } from '../CodelistRadioGroup';

// Ref: https://bitbucket.org/ledidi-code/ledidi-java/src/df23c744d97277e5579f359e71e7c2cc23766616/lambda-java-projectservice/src/main/java/com/ledidi/prjcts/lambda/controller/methods/project/UpdateProject.java#lines-44
// Organization IDs that require approval flow when enabling codelist (HSØ organizations)
const APPROVAL_REQUIRED_ORG_IDS = [34, 35, 36, 37, 38, 39, 40, 41, 42];

interface Props {
	projectId: string;
	deleteModalVisible: boolean;
	onDelete: () => void;
	onClose: (success?: boolean) => void;
}

export function EditProjectModal({ projectId, deleteModalVisible, onDelete, onClose }: Props) {
	const isCodelistFeatureEnabled = useIsCodelistFeatureEnabled();
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const { translate } = useTranslation();

	const [{ data: projectMetadataDefinition }] = useProjectMetadataDefinition();
	const [modalElement, setModalElement] = useState<HTMLElement | null>(null);

	const getModalRef = useCallback((node: HTMLElement | null) => {
		if (!node) return;
		const modalBody = node.querySelector('.modal-body');
		if (modalBody) {
			setModalElement(modalBody as HTMLElement);
		}
	}, []);

	const project = useProjectById(projectId);
	const isProjectFrozen = project?.status === ProjectStatus.Frozen;
	const isProjectPendingAdminApproval = project?.status === ProjectStatus.PendingAdminApproval;
	const isProjectRejected = project?.status === ProjectStatus.Rejected;

	const userQuery = useGetUserQuery();
	const currentUserOrgId = userQuery.data?.user.organizationId;
	const isApprovalRequiredOrg =
		currentUserOrgId !== null &&
		currentUserOrgId !== undefined &&
		APPROVAL_REQUIRED_ORG_IDS.includes(currentUserOrgId);

	const [endDateError, setEndDateError] = useState<string>(() => {
		if (!project || !project.projectStartDate || !project.projectEndDate) return '';

		const startDate = new Date(project.projectStartDate);
		startDate.setHours(0, 0, 0, 0);
		const projectEndDate = new Date(new Date(project.projectEndDate).setHours(0, 0, 0, 0));
		if (projectEndDate < startDate) {
			return translate(dict => dict.createProject.projectEndDateError);
		}

		return '';
	});

	const { hasEditProjectWriteAccess } = usePermissions({
		projectId: project?.projectId
	});
	const isProjectOwner = useIsProjectOwner(project?.projectId);

	const [{ loading: updatingProject, error: errorUpdatingProject }, updateProject] =
		useUpdateProject();

	const initialMetadataValues = getInitialMetadataFormValues(projectMetadataDefinition, project);

	const initialValues: ProjectFormValues = {
		projectId: project?.projectId ?? '',
		projectName: project?.projectName ?? '',
		givenProjectNumber: project?.givenProjectNumber ?? '',
		status: project?.status ?? ProjectStatus.Ongoing,
		description: project?.description ?? '',
		slideFolderURL: project?.slideFolderURL ?? '',
		projectType: ProjectType.CORE,
		projectEndDate: project?.projectEndDate ?? '',
		codelistEnabled: project?.keyId ? 'true' : 'false',
		...initialMetadataValues
	};

	const {
		register,
		setValue,
		getValues,
		handleSubmit,
		formState: { isDirty, isValid },
		control,
		watch
	} = useForm({
		defaultValues: initialValues,
		reValidateMode: 'onChange',
		mode: 'onChange',
		criteriaMode: 'all'
	});

	const { errors, requiredFields } = validateProjectForm(
		projectMetadataDefinition,
		watch,
		translate
	);

	const formHasChanges = isDirty;
	const formHasErrors = Object.keys(errors).length > 0;
	const schema = projectMetadataDefinition?.metadataSchema;

	const handleOnSubmit = handleSubmit(() => {
		if (formHasChanges && !updatingProject && hasEditProjectWriteAccess) {
			trimFields();

			const values = { ...getValues() };

			const parsedValues = { ...values };

			if (
				!schema?.if?.properties.projectType.enum.includes(
					String(values['metadata_projectType'])
				)
			) {
				parsedValues['metadata_archiveNumber'] =
					values['metadata_archiveNumber']?.toString() ?? '';
			}

			updateProject(parseFormValuesToEditedProject(parsedValues, projectMetadataDefinition));
		}
	});

	function trimFields() {
		const values = { ...getValues() };

		setValue('projectName', values.projectName.toString().trim());
		setValue('description', values.description.toString().trim());
		setValue('givenProjectNumber', values.givenProjectNumber.toString().trim());

		projectMetadataDefinition.metadataDefinition &&
			projectMetadataDefinition.metadataDefinition.forEach(metadataParameter => {
				if (values[metadataParameter.name])
					setValue(
						`${metadataParameter.name}`,
						values[metadataParameter.name].toString().trim()
					);
			});
	}

	// close modal on project update
	useCompletedAction(updatingProject, errorUpdatingProject, () => onClose(true));

	useKeyPress(
		{
			onEnterKeyPress: deleteModalVisible ? undefined : handleOnSubmit,
			onDeleteKeyPress: () => (isProjectOwner && !isProjectFrozen ? onDelete() : undefined)
		},
		{ listen: !updatingProject }
	);

	function checkProjectEndDate(dateStr: string) {
		if (!project?.projectStartDate) return;

		if (dateStr === '' && endDateError !== '') {
			setEndDateError('');
			return;
		}
		const date = new Date(dateStr);
		date.setHours(0, 0, 0, 0);
		const projectEndDate = new Date(new Date(project?.projectStartDate).setHours(0, 0, 0, 0));
		if (date < projectEndDate) {
			setEndDateError(translate(dict => dict.createProject.projectEndDateError));
		} else {
			if (endDateError.length > 0) {
				setEndDateError('');
			}
		}
	}

	const baseDictKey = EditProject.project;

	const statusOptions = [
		{
			label: translate(dict => dict[baseDictKey].status.values.ongoing),
			value: ProjectStatus.Ongoing
		},
		{
			label: translate(dict => dict[baseDictKey].status.values.onHold),
			value: ProjectStatus.OnHold
		},
		{
			label: translate(dict => dict[baseDictKey].status.values.ended),
			value: ProjectStatus.Ended
		}
	];

	const formMetadataParameters = transforMetadataParametersToFormRows(projectMetadataDefinition);
	const cantEditStatus =
		isProjectOwner && (isProjectFrozen || isProjectPendingAdminApproval || isProjectRejected);

	return (
		<Modal
			title={translate(dict => dict[baseDictKey].modalTitle)}
			primary={
				hasEditProjectWriteAccess && {
					label: translate(({ buttons }) =>
						formHasChanges ? buttons.update : buttons.done
					),
					loading: updatingProject,
					disabled: formHasChanges ? !isValid || formHasErrors : false,
					onClick: formHasChanges ? handleOnSubmit : onClose
				}
			}
			neutral={
				formHasChanges && {
					label: translate(({ buttons }) => buttons.cancel),
					onClick: onClose
				}
			}
			modalRef={getModalRef}
			onClose={onClose}
			visible
			close
		>
			<Form onSubmit={handleOnSubmit}>
				<Input
					{...register('projectName')}
					type={InputType.Text}
					label={translate(dict => dict[baseDictKey].title)}
					error={errors.projectName?.message as string}
					readOnly={!hasEditProjectWriteAccess}
					onBlur={trimFields}
					required
				/>
				<Spacer size={s => s.s} />
				<Input
					{...register('description')}
					type={InputType.Textarea}
					label={translate(dict => dict[baseDictKey].description)}
					rows={4}
					readOnly={!hasEditProjectWriteAccess}
					onBlur={trimFields}
				/>
				<Spacer size={s => s.s} />
				<Row>
					<Column>
						<Input
							{...register('givenProjectNumber')}
							type={InputType.Text}
							label={translate(dict => dict[baseDictKey].number)}
							readOnly={!hasEditProjectWriteAccess}
							onBlur={trimFields}
						/>
					</Column>
					<Column>
						<Controller
							control={control}
							name="projectEndDate"
							render={({ field: { value } }) => (
								<Input
									openDateDownwards
									type={InputType.Date}
									value={value as number}
									onBlur={trimFields}
									label={translate(dict => dict[baseDictKey].endDate)}
									error={endDateError}
									onDateChange={({ formattedDate }) => {
										checkProjectEndDate(formattedDate);
										setValue('projectEndDate', formattedDate, {
											shouldDirty: true
										});
									}}
									readOnly={!hasEditProjectWriteAccess}
								/>
							)}
						/>
					</Column>
				</Row>
				<Spacer size={s => s.s} />
				<Controller
					name="status"
					control={control}
					render={({ field: { name, value, onBlur } }) => (
						<RadioGroupUncontrolled
							name="status"
							label={translate(dict => dict[baseDictKey].status.label)}
							options={statusOptions}
							disabled={!hasEditProjectWriteAccess || cantEditStatus}
							// CONTROLLED PROPS
							value={value as string}
							onBlur={onBlur}
							onChange={newValue =>
								setValue(name, newValue as ProjectStatus, {
									shouldDirty: true
								})
							}
						/>
					)}
				/>

				{formMetadataParameters.length > 0 && (
					<>
						<Spacer size={s => s.s} />
						{formMetadataParameters.map((metadataParametersRow, i) => {
							return (
								<div key={i}>
									<Spacer size={s => s.s} />
									<Row>
										{metadataParametersRow.map(metadataParameter => (
											<Column key={metadataParameter.name}>
												{metadataParameter.type ===
													ProjectMetadataType.String && (
													<Input
														{...register(`${metadataParameter.name}`)}
														placeholder={translate(
															() => metadataParameter.placeholder
														)}
														type={InputType.Text}
														label={translate(
															() => metadataParameter.label
														)}
														error={
															errors[metadataParameter.name]?.message
														}
														tooltipComponent={
															<InfoTooltip
																marginOffset={{
																	left: 0.4,
																	bottom: 0.4
																}}
																iconVisible
																text={translate(
																	() =>
																		metadataParameter.description
																)}
																renderContext={
																	modalElement ?? undefined
																}
															/>
														}
														onBlur={trimFields}
														readOnly={!hasEditProjectWriteAccess}
														required={requiredFields.has(
															metadataParameter.name
														)}
													/>
												)}

												{metadataParameter.type ===
													ProjectMetadataType.Dropdown && (
													<Controller
														name={`${metadataParameter.name}`}
														control={control}
														render={({ field: { value, onBlur } }) => (
															<CreatableSelect
																label={translate(
																	() => metadataParameter.label
																)}
																tooltipPlace="top"
																tooltipComponent={
																	<InfoTooltip
																		marginOffset={{
																			left: 0.4,
																			bottom: 0.4
																		}}
																		iconVisible
																		text={translate(
																			() =>
																				metadataParameter.description
																		)}
																		renderContext={
																			modalElement ??
																			undefined
																		}
																	/>
																}
																placeholder={translate(
																	() =>
																		metadataParameter.placeholder
																)}
																value={
																	value
																		? {
																				value: value.toString(),
																				label: translate(
																					() =>
																						metadataParameter.categories.find(
																							p =>
																								p.id ===
																								value
																						)?.name ??
																						''
																				)
																		  }
																		: undefined
																}
																items={metadataParameter.categories
																	.filter(i => i.active)
																	.map(option => ({
																		label: translate(
																			() => option.name
																		),
																		value: `${option.id}`
																	}))}
																error={
																	errors[metadataParameter.name]
																		?.message
																}
																onClear={() => {
																	setValue(
																		`${metadataParameter.name}`,
																		'',
																		{
																			shouldDirty: true
																		}
																	);
																}}
																borderError={
																	!!errors[metadataParameter.name]
																		?.message
																}
																required={requiredFields.has(
																	metadataParameter.name
																)}
																onValueSelected={selectedValue =>
																	selectedValue &&
																	setValue(
																		`${metadataParameter.name}`,
																		selectedValue,
																		{
																			shouldDirty: true
																		}
																	)
																}
																readOnly={
																	!hasEditProjectWriteAccess
																}
																hint={translate(
																	() =>
																		metadataParameter.categories.find(
																			p => p.id === value
																		)?.description ?? ''
																)}
																onBlur={onBlur}
																scrollIntoView
															/>
														)}
													/>
												)}
											</Column>
										))}
									</Row>
									<Spacer size={s => s.s} />
									<InfoMessage
										message={
											<div>
												<p>
													{translate(
														dict => dict.projectMetadata.hso.info
													)}
												</p>
												<p>
													{translate(
														dict =>
															dict.projectMetadata.hso.byProceeding
													)}
												</p>
											</div>
										}
									/>
								</div>
							);
						})}
					</>
				)}

				{isCodelistFeatureEnabled && (
					<>
						<Spacer size={s => s.l} />

						{!project?.keyId &&
							watch('codelistEnabled') === 'true' &&
							isApprovalRequiredOrg && (
								<>
									<Warning message={translation.codelist.activationWarning} />
									<Spacer size={s => s.s} />
								</>
							)}

						<CodelistRadioGroup
							register={register}
							fieldName="codelistEnabled"
							disabled={!!project?.keyId}
						/>

						{!!project?.keyId && (
							<>
								<Spacer size={s => s.s} />

								<InfoMessage
									message={
										<div>
											<p>{translation.codelist.disableDescription}</p>
										</div>
									}
								/>
							</>
						)}

						<Spacer size={s => s.s} />
					</>
				)}
			</Form>

			{isProjectOwner && !isProjectFrozen && (
				<Flex flex={1} align={a => a.end} marginOffset={{ top: 2.4 }}>
					<Button
						title={translate(dict => dict[baseDictKey].delete)}
						variant={v => v.link}
						paddingOffset={{ all: 0 }}
						onClick={onDelete}
					/>
				</Flex>
			)}
		</Modal>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		codelist: {
			disableDescription:
				'You cannot deactivate the codelist after it has been enabled. Please contact support if you need to disable it.',
			activationWarning:
				'Activating this feature means the project needs to be re-approved. While waiting for approval, the project cannot be accessed. Approval is usually resolved within one business day.'
		}
	},
	[LanguageType.Norwegian]: {
		codelist: {
			disableDescription:
				'Du kan ikke deaktivere kodelisten etter at den er aktivert. Kontakt support hvis du trenger å deaktivere den.',
			activationWarning:
				'Aktivering av denne funksjonen betyr at prosjektet må godkjennes på nytt. Mens du venter på godkjenning, kan du ikke lenger aksessere prosjektet. Godkjenning skjer vanligvis innen én virkedag.'
		}
	}
};
